import React, { useCallback, useState } from 'react';
import {
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  ModalBody,
  ModalFooter,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Select,
  Textarea
} from '@chakra-ui/react';
import { z } from 'zod';
import api from '@api/endpoints';
import { getUserById } from '@store/actions/usersActions';
import { useDispatch } from 'react-redux';
import { setAlert } from '@store/actions/alertActions';
import { ALERT_STATUS_TYPE as ALERT_STATUS_TYPE } from '@constants';

const AddFreeTransfers = ({ userId, onClose, creditorsList }) => {
  const dispatch = useDispatch();
  const [count, setCount] = useState();
  const [reason, setReason] = useState('');
  const [isInvalid, setIsInvalid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [expiryDate, setExpiryDate] = useState('');
  const [creditor, setCreditor] = useState(null);

  const handleCountInputChange = useCallback((value) => {
    setIsInvalid(false);
    setCount(Math.floor(Number(value)));
  }, []);

  const handleReasonInputChange = useCallback((e) => {
    setIsInvalid(false);
    setReason(e?.target?.value);
  }, []);

  const handleDateChange = useCallback((e) => {
    try {
      const date = z.string().date();
      date.parse(e?.target?.value);
      setIsInvalid(false);
      setExpiryDate(e?.target?.value);
    } catch (error) {
      setIsInvalid(true);
      setExpiryDate(e?.target?.value);
    }
  }, []);
  const handleCreditorChange = useCallback((e) => {
    setIsInvalid(false);
    setCreditor(e?.target?.value);
  }, []);
  const isFormValid = useCallback((formData) => {
    return formData?.count && formData?.reason?.length && formData?.expiryDate;
  }, []);

  const addFreeTransfers = useCallback(
    ({ count, reason, expiryDate, creditor }) => {
      setIsLoading(true);
      const reqBody = {
        user_ids: [userId],
        remarks: reason,
        expiry: expiryDate,
        number_of_free_transfers: count,
        creditor: creditor
      };
      api.user
        ?.addFreeTransfers(reqBody)
        .then(() => {
          onClose();

          dispatch(
            setAlert({
              title: `Added ${count} free transfer${
                count === 1 ? '' : 's'
              } to user account`,
              status: 'success'
            })
          );

          //refetch user details, to get user details with updated free transfer count
          dispatch(getUserById(userId));
        })
        .catch((error) => {
          dispatch(
            setAlert({
              title:
                error?.message ||
                'Failed tox` add free transfers to user account',
              status: ALERT_STATUS_TYPE.ERROR
            })
          );
        })
        .finally(() => setIsLoading(false));
    },
    [userId, onClose, dispatch]
  );

  const handleSubmit = useCallback(() => {
    const formData = {
      count,
      reason,
      expiryDate,
      creditor
    };

    if (!isFormValid(formData)) {
      setIsInvalid(true);
      return;
    }

    addFreeTransfers(formData);
  }, [count, reason, addFreeTransfers, isFormValid, expiryDate]);

  return (
    <ModalBody>
      <FormControl isInvalid={isInvalid}>
        <Flex>
          <Flex w={'40%'} justifyContent={'flex-end'}>
            <FormLabel
              alignSelf={'flex-end'}
              fontSize={'0.75rem'}
              fontWeight={'500'}
              mb={0}
              lineHeight={'2rem'}
              pr={2}
            >
              Count :
            </FormLabel>
          </Flex>
          <NumberInput
            size={'sm'}
            fontWeight={'600'}
            w={'60%'}
            bg={'shadedGray.400'}
            color="#111"
            border={0}
            placeholder={'Number of free transfers to add'}
            _placeholder={{ opacity: 1, color: 'shadedGray.600' }}
            required
            rounded={'md'}
            min={0}
            onChange={handleCountInputChange}
            value={count}
          >
            <NumberInputField value={count} rounded="md" />
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>
        </Flex>
        <Flex my={'1rem'}>
          <Flex
            w={'40%'}
            justifyContent={'flex-end'}
            height={'100%'}
            alignItems={'center'}
          >
            <FormLabel
              alignSelf={'flex-end'}
              fontSize={'0.75rem'}
              fontWeight={'500'}
              mb={0}
              lineHeight={'2rem'}
              pr={2}
            >
              Reason :
            </FormLabel>
          </Flex>
          <Textarea
            resize={'none'}
            size={'sm'}
            fontSize={'0.75rem'}
            fontWeight={'600'}
            w={'60%'}
            type="text"
            rounded="lg"
            bg={'shadedGray.400'}
            color={'shadedGray.800'}
            border={0}
            placeholder={''}
            _placeholder={{ opacity: 1, color: 'shadedGray.600' }}
            required
            value={reason}
            onChange={handleReasonInputChange}
          />
        </Flex>
        <Flex my={'1rem'}>
          <Flex
            w={'40%'}
            justifyContent={'flex-end'}
            height={'100%'}
            alignItems={'center'}
          >
            <FormLabel
              alignSelf={'flex-end'}
              fontSize={'0.75rem'}
              fontWeight={'500'}
              mb={0}
              lineHeight={'2rem'}
              pr={2}
            >
              Expiry Date :
            </FormLabel>
          </Flex>
          <Input
            resize={'none'}
            size={'sm'}
            fontSize={'0.75rem'}
            fontWeight={'600'}
            w={'60%'}
            type="text"
            rounded="lg"
            bg={'shadedGray.400'}
            color={'shadedGray.800'}
            border={0}
            placeholder={'YYYY-MM-DD'}
            _placeholder={{ opacity: 1, color: 'shadedGray.600' }}
            required
            value={expiryDate}
            onChange={handleDateChange}
          />
        </Flex>
        <Flex my={'1rem'}>
          <Flex
            w={'40%'}
            justifyContent={'flex-end'}
            height={'100%'}
            alignItems={'center'}
          >
            <FormLabel
              alignSelf={'flex-end'}
              fontSize={'0.75rem'}
              fontWeight={'500'}
              mb={0}
              lineHeight={'2rem'}
              pr={2}
            >
              Creditor :
            </FormLabel>
          </Flex>
          <Select
            resize={'none'}
            size={'sm'}
            fontSize={'0.75rem'}
            fontWeight={'600'}
            w={'60%'}
            type="text"
            rounded="lg"
            bg={'shadedGray.400'}
            color={'shadedGray.800'}
            border={0}
            _placeholder={{ opacity: 1, color: 'shadedGray.600' }}
            defaultValue="ADMIN"
            value={creditor}
            onChange={handleCreditorChange}
          >
            {creditorsList.map((creditor) => (
              <option key={creditor} value={creditor}>
                {creditor}
              </option>
            ))}
          </Select>
        </Flex>

        <FormErrorMessage textAlign={'center'} display={'block'}>
          Please enter count, reason and expiry-date in given format
        </FormErrorMessage>
      </FormControl>

      <ModalFooter justifyContent={'center'}>
        <Button
          colorScheme="brand"
          color="altGray.400"
          mr={3}
          size={'sm'}
          fontSize={'0.75rem'}
          type="submit"
          onClick={handleSubmit}
          isLoading={isLoading}
        >
          Submit Now
        </Button>
      </ModalFooter>
    </ModalBody>
  );
};

export default AddFreeTransfers;
